/**
 * Shared table styling constants for the Efforts tabs, copied verbatim from the
 * SalesPulse reference (SalesPulse_-_Performance_Dashboard_by_SFE.html →
 * .tbl / .table-wrap / .eff-trend / .pcpm-pill / #effDimSwitcher).
 *
 * Styling only. Non-component exports live here (separate from the component
 * exports in EffortsTableParts.jsx, named effortsTableStyles.jsx in the source
 * repo) to satisfy react-refresh.
 */

/** Outer table card: white, 16px radius, 1px #eef1f6 border, soft shadow. */
export const TABLE_CARD_SX = {
  bgcolor: '#fff',
  border: '1px solid #eef1f6',
  borderRadius: '16px',
  p: 2,
  boxShadow: '0 1px 3px rgba(16,30,54,.06)',
};

/** .table-wrap — bordered, rounded, scrollable box around the table. */
export const TABLE_WRAP_SX = {
  overflow: 'auto',
  border: '1px solid #E4E8EE',
  borderRadius: '12px',
};

/** .tbl thead th — reference header background + bold uppercase grey labels. */
export const HEADER_CELL_SX = {
  fontSize: '13px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.3px',
  color: '#6B7280',
  bgcolor: '#f4f7fc',
  borderBottom: '2px solid #e2e8f2',
  whiteSpace: 'nowrap',
  px: '10px',
};

/** Thin row borders + zebra + hover, applied on the <Table> via sx. */
export const TABLE_SX = {
  '& td, & th': { borderColor: '#E4E8EE' },
  '& tbody tr:nth-of-type(even)': { backgroundColor: '#fafcff' },
  '& tbody tr:hover': { backgroundColor: '#E8F0FE' },
};

/** Bold navy first column (.eff-trend td:first-child feel). */
export const FIRST_COL_SX = { fontWeight: 700, color: '#0F1E3D' };

/** Right-aligned tabular numbers. */
export const NUM_CELL_SX = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/**
 * Header cell for a column whose data the backend does not return yet: same
 * layout as HEADER_CELL_SX but dimmed, so the column is visible (header +
 * cells) while clearly marked unavailable. Pair with a header `title`
 * ("Data not available yet") and greyed "—" body cells.
 */
export const UNAVAIL_HEADER_SX = {
  ...HEADER_CELL_SX,
  color: '#aeb6c2',
  cursor: 'help',
};

/** Body cell for an unavailable column: muted "—", tabular, no wrap. */
export const UNAVAIL_CELL_SX = {
  ...NUM_CELL_SX,
  color: '#b4bcc8',
};

/** Pinned bold Total row (reference `.tbl tr.total`): light fill, bold text. */
export const TOTAL_ROW_SX = {
  '& td': {
    fontWeight: 800,
    color: '#0F1E3D',
    bgcolor: '#f4f7fc',
    borderTop: '2px solid #e2e8f2',
  },
};

// .pcpm-pill tones from the reference: color + bg + border (border = color @ .3).
export const PCT_TONES = {
  good: { color: '#16A34A', bg: 'rgba(22,163,74,.12)', border: 'rgba(22,163,74,.3)' },
  warn: { color: '#B45309', bg: 'rgba(245,158,11,.16)', border: 'rgba(245,158,11,.35)' },
  bad: { color: '#DC2626', bg: 'rgba(220,38,38,.12)', border: 'rgba(220,38,38,.3)' },
};

/** Percentage → tone. Default thresholds: >=90 green, 85-90 amber, <85 red. */
export function pctTone(value, { good = 90, warn = 85 } = {}) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  if (v >= good) return 'good';
  if (v >= warn) return 'warn';
  return 'bad';
}

/**
 * COLOURED-TEXT palette (the reference `.cf-*` / `.cft-*` classes), used where
 * the brief asks for coloured values rather than coloured cells. Indexed 0..3
 * = red / amber / green / dark-green, matching the HTML `CF_BG`/`CF_TX` order.
 */
export const CF_TEXT = ['#C23B3B', '#A5701A', '#2B8A4A', '#0A7A3B'];

/**
 * Coverage % → level 0..3 (reference `covLvl`): >95 dark-green, >=90 green,
 * >=85 amber, else red. null for non-numeric.
 */
export function covLevel(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n > 95 ? 3 : n >= 90 ? 2 : n >= 85 ? 1 : 0;
}

/**
 * Compliance % → level 0..3 (reference `compLvl`): >90 dark-green, >=85 green,
 * >=80 amber, else red. null for non-numeric.
 */
export function compLevel(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n > 90 ? 3 : n >= 85 ? 2 : n >= 80 ? 1 : 0;
}

/** Coloured-text colour for a coverage % (or undefined → no colour). */
export function covTextColor(v) {
  const lvl = covLevel(v);
  return lvl == null ? undefined : CF_TEXT[lvl];
}

/** Coloured-text colour for a compliance % (or undefined → no colour). */
export function compTextColor(v) {
  const lvl = compLevel(v);
  return lvl == null ? undefined : CF_TEXT[lvl];
}

// ── Effort ↔ Performance score bands (reference achSc/covSc/compSc) ──────────
/** Total/Power-brand achievement % → 1..5 score (reference `achSc`). */
export function achScore(a) {
  const n = Number(a);
  if (!Number.isFinite(n)) return 1;
  return n > 105 ? 5 : n >= 100 ? 4 : n >= 95 ? 3 : n >= 90 ? 2 : 1;
}
/** Coverage % → 1..5 score (reference `covSc`). */
export function coverageScore(c) {
  const n = Number(c);
  if (!Number.isFinite(n)) return 1;
  return n > 95 ? 5 : n >= 90 ? 4 : n >= 85 ? 3 : n >= 80 ? 2 : 1;
}
/** Compliance % → 1..5 score (reference `compSc`). */
export function complianceScore(c) {
  const n = Number(c);
  if (!Number.isFinite(n)) return 1;
  return n > 90 ? 5 : n >= 85 ? 4 : n >= 80 ? 3 : n >= 75 ? 2 : 1;
}
/** Achievement % coloured-text colour for the correlation table (reference achCf). */
export function achTextColor(a) {
  const n = Number(a);
  if (!Number.isFinite(n)) return undefined;
  return CF_TEXT[n > 105 ? 3 : n >= 100 ? 2 : n >= 95 ? 1 : 0];
}

/** Month short label from "YYYYMM" (e.g. "202504" -> "Apr 2025"), else passthrough. */
export function monthShortLabel(ym) {
  const s = String(ym ?? '').replace(/-/g, '');
  if (!/^\d{6}$/.test(s)) return String(ym ?? '');
  const year = Number(s.slice(0, 4));
  const month = Number(s.slice(4, 6));
  if (!year || month < 1 || month > 12) return String(ym ?? '');
  return `${new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'short' })} ${year}`;
}

/**
 * Full financial-year month axis Apr→Mar from a FY label like "2025-26" or
 * "2025-2026". Always 12 entries so no-data months still render as columns.
 * Falls back to `apiMonths` when the FY can't be parsed.
 */
export function buildFyMonths(fiscalYear, apiMonths) {
  const m = String(fiscalYear ?? '').match(/^(\d{4})-(\d{2,4})$/);
  if (!m) return apiMonths ?? [];
  const startYear = Number(m[1]);
  const out = [];
  for (let i = 0; i < 12; i += 1) {
    const monthNum = 4 + i; // Apr(4) .. Mar(next year)
    const year = startYear + Math.floor((monthNum - 1) / 12);
    const mm = ((monthNum - 1) % 12) + 1;
    const ym = `${year}${String(mm).padStart(2, '0')}`;
    out.push({ yearmonth: ym, label: monthShortLabel(ym) });
  }
  return out;
}

/** Tenure-band dropdown options (reference `#tenureSel`). */
export const TENURE_BANDS = [
  { value: 'all', label: 'All Tenures' },
  { value: 'lt6', label: '< 6 Months' },
  { value: '6to12', label: '6 M - 12 M' },
  { value: '1to2', label: '1 Yr - 2 Yrs' },
  { value: '2to3', label: '2 Yrs - 3 Yrs' },
  { value: '3to4', label: '3 Yrs - 4 Yrs' },
  { value: '4to5', label: '4 Yrs - 5 Yrs' },
  { value: 'gt5', label: '> 5 Yrs' },
];

/** True when tenure (years, 1-dec) falls in band `b` (reference tmatch). */
export function tenureInBand(years, b) {
  if (b === 'all') return true;
  if (years == null || !Number.isFinite(Number(years))) return false;
  const t = Number(years);
  switch (b) {
    case 'lt6': return t < 0.5;
    case '6to12': return t >= 0.5 && t < 1;
    case '1to2': return t >= 1 && t < 2;
    case '2to3': return t >= 2 && t < 3;
    case '3to4': return t >= 3 && t < 4;
    case '4to5': return t >= 4 && t < 5;
    case 'gt5': return t >= 5;
    default: return true;
  }
}

// Sub-pill toggle (#effDimSwitcher label): pill, blue idle, gradient selected.
export const SUBPILL_GROUP_SX = {
  flexWrap: 'wrap',
  gap: 0.75,
  '& .MuiToggleButtonGroup-grouped': {
    border: '1.5px solid #bcd5fb',
    borderRadius: '999px !important',
    mr: 0,
    px: 1.75,
    py: 0.5,
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 600,
    color: '#2554a8',
    bgcolor: '#eef4ff',
    '&:hover': { bgcolor: '#dbe8ff', borderColor: '#8fbaf6' },
    '&.Mui-selected': {
      color: '#fff',
      borderColor: '#1657c8',
      background: 'linear-gradient(135deg,#2E7CF6,#1657c8)',
      boxShadow: '0 3px 9px rgba(46,124,246,.4)',
      '&:hover': { background: 'linear-gradient(135deg,#2E7CF6,#1657c8)' },
    },
    '&.Mui-disabled': { color: '#9aa3b2', bgcolor: '#f3f4f6', borderColor: '#e2e8f0' },
  },
};
